import apiV2 from '../api/v2';
import type { AxiosError } from 'axios';
import { toDmError } from './dmErrors';

export interface DmPatient {
    id: number;
    externalId: string | null;
    status: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    /** Current scan interval in days, from DM's monitoring.config.interval_days. */
    intervalDays: number | null;
    /** When true, interval changes must also carry requestControlScanAfterDays.
     *  Optional: tolerates a backend deployed before this field existed. */
    dynamicAlignerChange?: boolean;
    /** Days between a NO-GO and the follow-up scan (DAC protocols only). */
    requestControlScanAfterDays?: number | null;
    /** Monitoring details for the DM control panel; optional for older backends. */
    productName?: string | null;
    protocolTitle?: string | null;
    /** ISO timestamps. */
    startDate?: string | null;
    pauseDate?: string | null;
    resumeDate?: string | null;
    stopDate?: string | null;
    nextScanDate?: string | null;
    isAppActivated?: boolean;
    scanboxName?: string | null;
    hasScanboxLedSupport?: boolean | null;
    currentAligner?: number | null;
    totalAligners?: number | null;
    /** DM tells the patient to move to the next aligner at once; optional for older backends. */
    forceNextAlignerChange?: boolean;
    /** False for fixed-appliance patients: no arch is on aligners. Optional for older backends. */
    tracksAligners?: boolean;
    /** DM's treatment label per arch, e.g. "Orthodontic pre-treatment - Fixed appliance - Other". */
    treatment?: { upper: string | null; lower: string | null };
}

export interface DmPatientResponse {
    data: DmPatient | null;
}

export interface ActivationLinkResponse {
    count: number;
}

export interface CreateDmPatientBody {
    firstName: string;
    lastName: string;
    email: string;
    dateOfBirth?: string;
    phone?: string;
}

interface DuplicateInfo {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
}

/**
 * Backend's standard error envelope serializes AppError as `{ code, message, ... }`
 * via `result.error.toJSON()`, so `responseBody.error` can be either a string
 * (some controllers send the message directly) or an object. Normalize.
 */
function extractApiErrorMessage(error: unknown): string | undefined {
    if (typeof error === 'string') return error;
    if (
        error !== null &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
    ) {
        return (error as { message: string }).message;
    }
    return undefined;
}

export type EmailCheckResult = { exists: false } | { exists: true; dmPatient: DuplicateInfo };

export type CreateDmPatientResult =
    | { success: true; dmPatient: DmPatient }
    | { success: false; isDuplicate: true; dmPatient: DuplicateInfo; message: string }
    | { success: false; isDuplicate: false; message: string };

export async function getDmPatient(patientId: string): Promise<DmPatient | null> {
    try {
        const response = await apiV2.get<{ success: boolean; data: DmPatient | null }>(
            `/patients/${patientId}/dm/patient`
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function createDmPatient(
    patientId: string,
    body: CreateDmPatientBody
): Promise<CreateDmPatientResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: DmPatient }>(
            `/patients/${patientId}/dm/patient`,
            body
        );
        return { success: true, dmPatient: response.data.data };
    } catch (err) {
        const axiosError = err as AxiosError<{
            success: boolean;
            error: string | { code?: string; message?: string };
            data?: { dmPatient: DuplicateInfo };
        }>;
        const status = axiosError.response?.status;
        const responseBody = axiosError.response?.data;

        if (status === 409 && responseBody?.data?.dmPatient) {
            return {
                success: false,
                isDuplicate: true,
                dmPatient: responseBody.data.dmPatient,
                message: extractApiErrorMessage(responseBody.error) ?? 'Email already exists in DM.'
            };
        }

        const message =
            extractApiErrorMessage(responseBody?.error) ??
            (err instanceof Error ? err.message : 'An unexpected error occurred.');

        return { success: false, isDuplicate: false, message };
    }
}

export async function checkDmPatientEmail(
    patientId: string,
    email: string
): Promise<EmailCheckResult> {
    try {
        const response = await apiV2.get<{
            success: boolean;
            data: EmailCheckResult;
        }>(`/patients/${patientId}/dm/check-email`, { params: { email } });
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function sendActivationLink(patientId: string): Promise<ActivationLinkResponse> {
    try {
        const response = await apiV2.post<{ success: boolean; data: ActivationLinkResponse }>(
            `/patients/${patientId}/dm/monitoring/activation-link`
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function updateScanboxLedSupport(
    patientId: string,
    hasScanboxLedSupport: boolean
): Promise<{ hasScanboxLedSupport: boolean }> {
    try {
        const response = await apiV2.patch<{
            success: boolean;
            data: { hasScanboxLedSupport: boolean };
        }>(`/patients/${patientId}/dm/scanbox`, { hasScanboxLedSupport });
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function unlinkDmPatient(patientId: string): Promise<{ cleared: true }> {
    try {
        const response = await apiV2.post<{ success: boolean; data: { cleared: true } }>(
            `/patients/${patientId}/dm/unlink`
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export type LinkExistingResult =
    | { success: true; dmPatient: DmPatient }
    | { success: false; reason: 'not_found'; message: string }
    | { success: false; reason: 'already_linked'; message: string }
    | {
          success: false;
          reason: 'already_linked_elsewhere';
          message: string;
          otherPatientId?: string;
      }
    | { success: false; reason: 'error'; message: string };

export async function linkExistingDmPatient(
    patientId: string,
    email: string
): Promise<LinkExistingResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: DmPatient }>(
            `/patients/${patientId}/dm/link-existing`,
            { email }
        );
        return { success: true, dmPatient: response.data.data };
    } catch (err) {
        const axiosError = err as AxiosError<{
            success: boolean;
            error: string | { code?: string; message?: string; details?: { patientId?: string } };
            data?: { patientId?: string };
        }>;
        const status = axiosError.response?.status;
        const responseBody = axiosError.response?.data;
        const message =
            extractApiErrorMessage(responseBody?.error) ??
            (err instanceof Error ? err.message : 'An unexpected error occurred.');

        if (status === 404) {
            return { success: false, reason: 'not_found', message };
        }

        if (status === 409) {
            const details =
                responseBody?.error !== null &&
                typeof responseBody?.error === 'object' &&
                'details' in (responseBody.error as object)
                    ? (responseBody.error as { details?: { patientId?: string } }).details
                    : undefined;

            const otherPatientId = details?.patientId ?? responseBody?.data?.patientId;

            if (otherPatientId) {
                return {
                    success: false,
                    reason: 'already_linked_elsewhere',
                    message,
                    otherPatientId
                };
            }
            return { success: false, reason: 'already_linked', message };
        }

        return { success: false, reason: 'error', message };
    }
}
